import { useState } from 'react';
import { Loader2, ArrowRight, AlertTriangle, X } from 'lucide-react';
import { useAppStore } from '@/store/appStore';
import { ActionGridRow } from '@/types';

interface MoveActionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedActions: ActionGridRow[];
    onConfirmMove: (targetClientId: string) => Promise<void>;
    clients: { id: string; name: string }[];
}

export function MoveActionsModal({
    isOpen,
    onClose,
    selectedActions,
    onConfirmMove,
    clients,
}: MoveActionsModalProps) {
    const [targetClientId, setTargetClientId] = useState<string>('');
    const [isMoving, setIsMoving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { selectedCountry } = useAppStore();

    const handleConfirm = async () => {
        if (!targetClientId) {
            setError('Please select a target client');
            return;
        }

        setIsMoving(true);
        setError(null);
        try {
            await onConfirmMove(targetClientId);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Error moving actions');
        } finally {
            setIsMoving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-50 rounded-full">
                            <ArrowRight className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#454240]">Move Actions</h3>
                            <p className="text-xs text-gray-500">
                                Moving {selectedActions.length} action{selectedActions.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">

                    {error && (
                        <div className="flex items-start gap-3 p-3 text-sm text-red-600 bg-red-50 rounded-md">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-[#454240] mb-2">
                                Select Target Client
                            </label>
                            <select
                                value={targetClientId}
                                onChange={(e) => setTargetClientId(e.target.value)}
                                className="w-full h-10 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#eb4a4a] focus:border-transparent transition-all shadow-sm"
                                disabled={isMoving}
                            >
                                <option value="" disabled>Select a client...</option>
                                {clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.name}
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1.5 text-xs text-gray-500">
                                Actions will be moved to this client in <strong>{selectedCountry}</strong>.
                            </p>
                        </div>

                        <div className="bg-blue-50/50 rounded-md p-4 border border-blue-100">
                            <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">Selected Actions</h4>
                            <ul className="space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                                {selectedActions.map(action => (
                                    <li key={action.id} className="text-xs text-blue-900 flex items-center justify-between">
                                        <span className="truncate flex-1 font-medium">{action.vcpConcept || '(No Description)'}</span>
                                        <span className="text-blue-400 ml-2">#{action.rowNumber}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="flex items-start gap-2 text-xs text-gray-500 italic bg-gray-50 p-3 rounded border border-gray-100">
                            <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                            <p>
                                Moving actions will create new records in the destination client and mark the originals as 'Eliminated'.
                                <strong>This cannot be undone easily.</strong>
                            </p>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <button
                        onClick={onClose}
                        disabled={isMoving}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors shadow-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isMoving || !targetClientId}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#eb4a4a] rounded-md hover:bg-[#d93a3a] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#eb4a4a] transition-all shadow-sm"
                    >
                        {isMoving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Moving...</span>
                            </>
                        ) : (
                            <>
                                <span>Confirm Move</span>
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
